import React from 'react';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AdminOverviewPage from './AdminOverviewPage';
import JobAdminPage from './JobAdminPage';
import ExportAdminPage from './ExportAdminPage';
import PatternMarketDataAdminPage from './PatternMarketDataAdminPage';
import PatternReplayAdminPage from './PatternReplayAdminPage';
import MailCenterAdminPage from './MailCenterAdminPage';
import PatternPerformanceAdminPage from './PatternPerformanceAdminPage';
import PatternScannerPageViewsAdminPage from './PatternScannerPageViewsAdminPage';
import SystemSettingsAdminPage from './SystemSettingsAdminPage';
import EngineComparisonLabPage from './EngineComparisonLabPage';
import MonitoringAdminPage from './MonitoringAdminPage';
import TelegramPublicationRulesAdminPage from './TelegramPublicationRulesAdminPage';
import TradingAdminPage from './TradingAdminPage';

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user')) || null;
  } catch {
    return null;
  }
}

const priorityMenu = [
  {
    label: 'Pattern Performance',
    path: '/admin/pattern-performance',
    description: 'Benchmark des versions du moteur.',
    icon: <AnalyticsRoundedIcon />,
  },
  {
    label: 'Monitoring',
    path: '/admin/monitoring',
    description: 'Supervision des schedulers et alertes.',
    icon: <SpeedRoundedIcon />,
  },
  {
    label: 'Jobs',
    path: '/admin/job',
    description: 'Déclencheurs manuels backend.',
    icon: <SettingsSuggestRoundedIcon />,
  },
  {
    label: 'Engine Comparison Lab',
    path: '/admin/engine-comparison-lab',
    description: 'Comparaison multi-engine read-only.',
    icon: <HubRoundedIcon />,
  },
  {
    label: 'Ordres BloFin',
    path: '/trading/blofin',
    description: 'Ordres manuels BloFin — accès administrateur.',
    icon: <AccountBalanceWalletRoundedIcon />,
  },
  {
    label: 'Trading (SHADOW)',
    path: '/admin/trading',
    description: 'Positions et moteur de risque — aucun ordre réel.',
    icon: <CandlestickChartRoundedIcon />,
  },
];

const secondaryMenu = [
  {
    label: 'Vue d’ensemble',
    path: '/admin/overview',
    description: 'Synthèse des contacts et incidents récents.',
    icon: <DashboardRoundedIcon />,
  },
  {
    label: 'Mail Center',
    path: '/admin/mail-center',
    description: 'Contacts et historique des emails.',
    icon: <MailOutlineRoundedIcon />,
  },
  {
    label: 'Export CSV',
    path: '/admin/export',
    description: 'Snapshots vers src/main/resources/import/.',
    icon: <SaveRoundedIcon />,
  },
  {
    label: 'Pattern Market Data',
    path: '/admin/pattern-market-data',
    description: 'Bootstrap, synchro et snapshots CSV.',
    icon: <CandlestickChartRoundedIcon />,
  },
  {
    label: 'Pattern Replay',
    path: '/admin/pattern-replay',
    description: 'Relecture de fenêtres historiques.',
    icon: <ReplayRoundedIcon />,
  },
  {
    label: 'Logs Scanner',
    path: '/admin/pattern-scanner-views',
    description: 'Consultations Pattern Scanner.',
    icon: <HistoryRoundedIcon />,
  },
  {
    label: 'Telegram · Règles de publication',
    path: '/admin/telegram-publication-rules',
    description: 'Politique de publication Telegram.',
    icon: <SendRoundedIcon />,
  },
  {
    label: 'Paramètres',
    path: '/admin/settings',
    description: 'Réglages système et bascules.',
    icon: <TuneRoundedIcon />,
  },
];

function AdminToolCard({ item, location }) {
  const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
  return (
    <Box component={NavLink} to={item.path} sx={{ textDecoration: 'none' }}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 3,
          background: active ? 'rgba(247,243,234,0.14)' : 'rgba(255,255,255,0.04)',
          border: active ? '1px solid rgba(247,243,234,0.28)' : '1px solid rgba(247,243,234,0.08)',
          color: '#f7f3ea',
          transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            background: 'rgba(247,243,234,0.1)',
            borderColor: 'rgba(247,243,234,0.22)',
          },
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: active ? 'rgba(247,243,234,0.16)' : 'rgba(255,255,255,0.06)',
              color: active ? '#f3c65c' : '#f7f3ea',
              flexShrink: 0,
            }}
          >
            {item.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#f7f3ea', mb: 0.15 }}>
              {item.label}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: 'rgba(247,243,234,0.74)' }}>
              {item.description}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

const AdminPage = () => {
  const location = useLocation();
  const storedUser = readStoredUser();

  if (!storedUser || String(storedUser.role || '').toUpperCase() !== 'ADMIN') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <Box sx={{ p: 3, background: 'linear-gradient(180deg, #efe8dc 0%, #edf3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={2.5}>
        <Paper
          sx={{
            width: '100%',
            p: 2.5,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)',
            color: '#f7f3ea',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              icon={<AdminPanelSettingsRoundedIcon sx={{ color: '#f7f3ea !important' }} />}
              label="Admin Console"
              sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Pilotage admin
            </Typography>
          </Stack>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.2,
            }}
          >
            {priorityMenu.map((item) => (
              <AdminToolCard key={item.path} item={item} location={location} />
            ))}
          </Box>

          <Accordion
            elevation={0}
            defaultExpanded={false}
            sx={{
              mt: 2,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(247,243,234,0.1)',
              color: '#f7f3ea',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#f7f3ea' }} />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Autres outils d’administration
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <List dense disablePadding>
                {secondaryMenu.map((item) => (
                  <ListItemButton key={item.path} component={NavLink} to={item.path} sx={{ borderRadius: 2, color: '#f7f3ea', '&:hover': { background: 'rgba(247,243,234,0.1)' } }}>
                    <ListItemIcon sx={{ color: '#f7f3ea', minWidth: 38 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={item.description}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 700, color: '#f7f3ea' }}
                      secondaryTypographyProps={{ variant: 'caption', color: 'rgba(247,243,234,0.74)' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
        <Routes>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<AdminOverviewPage />} />
          <Route path="mail-center" element={<MailCenterAdminPage />} />
          <Route path="job" element={<JobAdminPage />} />
          <Route path="export" element={<ExportAdminPage />} />
          <Route path="pattern-market-data" element={<PatternMarketDataAdminPage />} />
          <Route path="pattern-performance" element={<PatternPerformanceAdminPage />} />
          <Route path="engine-comparison-lab" element={<EngineComparisonLabPage />} />
          <Route path="pattern-replay" element={<PatternReplayAdminPage />} />
          <Route path="pattern-scanner-views" element={<PatternScannerPageViewsAdminPage />} />
          <Route path="monitoring" element={<MonitoringAdminPage />} />
          <Route path="telegram-publication-rules" element={<TelegramPublicationRulesAdminPage />} />
          <Route path="trading" element={<TradingAdminPage />} />
          <Route path="settings" element={<SystemSettingsAdminPage />} />
        </Routes>
        </Box>
      </Stack>
    </Box>
  );
};

export default AdminPage;
